import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Send, ArrowLeft, CheckCircle, AlertCircle, Mail, User, MessageSquare, FileText
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrorMsg("Please fill in all required fields.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to send enquiry");
      }
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 no-underline" data-testid="contact-logo-link">
            <div className="w-8 h-8 rounded-md bg-[#FF7300] flex items-center justify-center">
              <span className="text-white font-display font-bold text-sm">A</span>
            </div>
            <span className="font-display font-bold text-[#09090B] text-base tracking-tight">
              AmznAWP
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#09090B] transition-colors no-underline"
            data-testid="contact-back-link"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-28 pb-20 px-6 md:px-12">
        <div className="max-w-xl mx-auto">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 rounded-full bg-[#FF7300]/10 flex items-center justify-center mx-auto mb-4">
              <MessageSquare size={28} className="text-[#FF7300]" />
            </div>
            <h1 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-3">
              Contact Us
            </h1>
            <p className="text-[#52525B] text-base max-w-md mx-auto">
              Have a question, feature request, or need support? We'd love to hear from you.
            </p>
          </div>

          {/* Success State */}
          {status === "success" ? (
            <div className="bg-[#e7f7e7] border border-[#067d06]/20 rounded-xl p-8 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-[#067d06]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-[#067d06]" />
              </div>
              <h2 className="font-display font-bold text-xl text-[#067d06] mb-2">Message Sent!</h2>
              <p className="text-sm text-[#52525B] mb-6">
                Thank you for reaching out. We'll get back to you within 24–48 hours.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="inline-flex items-center gap-2 bg-[#09090B] text-white rounded-md px-6 py-2.5 text-sm font-semibold hover:bg-[#27272A] transition-colors cursor-pointer border-0"
                data-testid="contact-send-another-btn"
              >
                <Send size={14} /> Send Another Message
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              {/* Error */}
              {status === "error" && (
                <div className="bg-[#fee2e2] border border-[#b12704]/20 rounded-lg p-3 flex items-start gap-2" data-testid="contact-error">
                  <AlertCircle size={16} className="text-[#b12704] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-[#b12704]">{errorMsg}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
                  <User size={14} className="text-[#FF7300]" /> Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact-name"
                  data-testid="contact-input-name"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
                  <Mail size={14} className="text-[#FF7300]" /> Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-subject" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
                  <FileText size={14} className="text-[#FF7300]" /> Subject
                </Label>
                <Input
                  id="contact-subject"
                  data-testid="contact-input-subject"
                  placeholder="e.g. License issue, Feature request..."
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value)}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact-message" className="text-sm font-semibold text-[#09090B] flex items-center gap-2">
                  <MessageSquare size={14} className="text-[#FF7300]" /> Message <span className="text-red-500">*</span>
                </Label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  rows={5}
                  placeholder="Tell us how we can help..."
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  required
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full flex items-center justify-center gap-2 bg-[#FF7300] text-white hover:bg-[#E66800] transition-all rounded-md px-6 py-3 text-sm font-bold border-0 cursor-pointer disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                <Send size={14} />
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>

              <p className="text-xs text-[#A1A1AA] text-center">
                Or email us directly at{" "}
                <a href="mailto:info@amznawp.com" className="text-[#FF7300] hover:underline">
                  info@amznawp.com
                </a>
              </p>
            </form>
          )}
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-[#E4E4E7] py-6 px-6 text-center">
        <p className="text-xs text-[#A1A1AA]">
          AmznAWP &middot;{" "}
          <a href="mailto:info@amznawp.com" className="text-[#FF7300] hover:underline">
            info@amznawp.com
          </a>
        </p>
      </footer>
    </div>
  );
}
